import { cn } from "@/lib/utils";
import { panelRadiusClass } from "../chat/branding";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
} from "@/components/molecules/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { usePublicTranslation } from "@/features/utils/i18n";
import { CalendarIcon, SignInIcon, UserIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { type BookingEvent } from "../../hooks/use-public-booking";
import { usePublicCustomer } from "../../hooks/use-public-customer";
import { type PublicSessionInfo } from "../../hooks/use-public-session-info";
import { CalendarEventAttachment } from "../attachments";
import { LanguageSelector } from "../common/LanguageSelector";
import { ConfirmCustomerInfo } from "./ConfirmCustomerInfo";
import { LoginDialog } from "./LoginDialog";

interface ClientInfoDialogProps {
  publicId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionInfo: PublicSessionInfo | undefined;
  onPhoneHandoff?: (handoff: { phone: string }) => void;
  events: BookingEvent[] | undefined;
  onEventClick: (event: BookingEvent) => void;
}

export function ClientInfoDialog({
  publicId,
  open,
  onOpenChange,
  sessionInfo,
  onPhoneHandoff,
  events,
  onEventClick,
}: ClientInfoDialogProps) {
  const { t } = usePublicTranslation();
  const { data: customer } = usePublicCustomer(publicId);
  const [showConfirmInfo, setShowConfirmInfo] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const futureEvents = events?.filter((event) => event.start_time > Date.now());

  // Identified = the visitor has a proven contact channel (verified phone or
  // email). Until then the only meaningful action is to sign in via phone OTP;
  // once identified, they can complete their profile with the other channel.
  const isIdentified =
    !!customer?.phone_is_verified || !!customer?.email_is_verified;

  return (
    <>
      <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
        <ResponsiveDialogContent className={cn("sm:max-w-[525px]", panelRadiusClass)}>
          <ResponsiveDialogHeader>
            <div className="flex items-center gap-3">
              {sessionInfo?.client.logo && (
                <img
                  src={sessionInfo.client.logo}
                  alt={sessionInfo.client.name}
                  className="size-12 rounded-lg object-cover"
                />
              )}
              <div>
                <h2 className="text-lg font-semibold">
                  {sessionInfo?.client.name || t("client_info.loading")}
                </h2>
              </div>
            </div>
          </ResponsiveDialogHeader>

          <div className="px-4 pb-6 space-y-4">
            {sessionInfo?.client.address && (
              <div className="flex items-center text-muted-foreground">
                <span>{sessionInfo.client.address}</span>
              </div>
            )}

            {/* Language Selector */}
            <LanguageSelector />

            {/* Sign-in / complete-profile action */}
            <Separator />
            {isIdentified ? (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setShowConfirmInfo(true);
                  onOpenChange(false);
                }}
              >
                <UserIcon className="size-4 mr-2" />
                {t("client_info.complete_profile")}
              </Button>
            ) : (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setShowLogin(true);
                  onOpenChange(false);
                }}
              >
                <SignInIcon className="size-4 mr-2" />
                {t("client_info.connect")}
              </Button>
            )}

            {/* Events Section */}
            {futureEvents && futureEvents.length > 0 && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <CalendarIcon className="size-4" />
                    <span>{t("client_info.upcoming_appointments")}</span>
                  </div>
                  <div className="space-y-2">
                    {futureEvents.map((event) => (
                      <CalendarEventAttachment
                        key={event.id}
                        eventId={event.id}
                        events={events}
                        onEventClick={onEventClick}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      {/* Confirm Customer Info Dialog */}
      <ConfirmCustomerInfo
        publicId={publicId}
        open={showConfirmInfo}
        onOpenChange={setShowConfirmInfo}
        dismissible
      />

      {/* Sign-in (phone OTP) Dialog */}
      <LoginDialog
        publicId={publicId}
        open={showLogin}
        onOpenChange={setShowLogin}
        phoneHandoff={!!sessionInfo?.phoneHandoffEnabled}
        onCallStarted={(handoff) => {
          onOpenChange(false);
          onPhoneHandoff?.(handoff);
        }}
      />
    </>
  );
}
